import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Stack, Title, Text, Badge } from '@repletoryreach/sdk-ui';
import { MetricCard } from '@repletoryreach/sdk-ui/data-display';
import { billing, type broadcasts } from '@repletoryreach/sdk-api';
import { useCredits } from '../../../../lib/credits';
import { useT } from '../../../../i18n/I18nProvider';
import { formatCount, formatDateTime, formatRelative } from '../../../../lib/format';
import { isEmailBroadcast, isSmsBroadcast, smsKindName, emailKindName } from '../../components/channel';
import { broadcastStatusLabel, formatRate, progressOf, rate, spendLines } from '../../components/delivery';
import { ReportCard } from './ReportCard';

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

/** A broadcast's status colour: done green, failed red, cancelled orange, sending blue, waiting grey. */
export function statusColor(code: string): string {
  if (/^(complete|done|finished)/i.test(code)) return 'green';
  if (/^(cancel|abort|paused)/i.test(code)) return 'orange';
  if (/^(fail|error)/i.test(code)) return 'red';
  if (/^(running|in_progress|sending|started)/i.test(code)) return 'blue';
  return 'gray';
}

export interface OverviewTabProps {
  broadcast: broadcasts.Broadcast;
  report: broadcasts.BroadcastReport | null;
  reportLoading: boolean;
  /** How often the page is asking again, or false once it has settled. */
  poll: number | false;
  onShowRecipients: (status: string) => void;
}

export function OverviewTab({ broadcast, report, reportLoading, poll, onShowRecipients }: OverviewTabProps) {
  const t = useT();
  const credits = useCredits();
  const whatsapp = (broadcast.channel ?? 'whatsapp') === 'whatsapp';

  /*
   * What the campaign has cost, from the attributed ledger: every send names the broadcast it belongs to. Net of
   * refunds (a message that failed after it was charged is given back), and asked for as long as the page keeps
   * asking about the broadcast, since a late failure moves the figure after it finished.
   */
  const spendQ = useQuery({
    queryKey: ['usage', 'sources', 'broadcast', broadcast.id],
    queryFn: () => billing.getUsageSources('broadcast', [broadcast.id]),
    refetchInterval: poll === false ? false : Math.max(poll, 10_000),
    retry: false
  });
  const spendRow = spendQ.data?.data[0] ?? null;
  const spend = spendLines(t, credits, spendRow, broadcast.estimated_currency);

  // The funnel, from the report when it is in; from the row's counters until it is.
  const attempted = report?.attempted ?? broadcast.recipients_sent + broadcast.recipients_failed;
  const delivered = report ? report.delivered + report.read : broadcast.recipients_delivered ?? 0;
  const read = report?.read ?? broadcast.recipients_read ?? 0;
  const failed = report?.failed ?? broadcast.recipients_failed;
  const filtered = report?.filtered ?? broadcast.recipients_filtered;
  const deliveredRate = report ? report.delivered_rate : rate(delivered, attempted);
  const readRate = report ? report.read_rate : rate(read, attempted);
  const failedRate = report ? report.failed_rate : rate(failed, attempted);

  const progress = progressOf(broadcast, report);

  /*
   * Observed throughput and a rough finish time, from `started_at` and how many are done. Only while it is
   * actually running, and the time left counts only those still queued: a failure is as finished as a send.
   */
  const throughput = useMemo(() => {
    if (broadcast.status_code !== 'running' || !broadcast.started_at || progress.done === 0 || progress.queued === 0) return null;
    const elapsedMs = Date.now() - new Date(broadcast.started_at).getTime();
    if (elapsedMs < 5000) return null;   // too early to be a rate
    const perMinute = Math.max(1, Math.round((progress.done / elapsedMs) * 60_000));
    return { perMinute, etaMinutes: Math.max(1, Math.ceil(progress.queued / perMinute)) };
  }, [broadcast.status_code, broadcast.started_at, progress.done, progress.queued]);

  const none = <span style={{ color: MUTED }}>{t('broadcasts.view.overview.notYet')}</span>;
  const when = (iso: string | null | undefined) => (iso ? `${formatDateTime(iso)} (${formatRelative(iso)})` : none);

  return (
    <Stack gap="md">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <MetricCard label={t('common.total')} value={formatCount(broadcast.recipients_total)} hint={t('broadcasts.view.overview.recipientsHint')} />
        <MetricCard
          label={t('broadcasts.recipient.delivered')}
          value={formatCount(delivered)}
          hint={deliveredRate != null ? t('broadcasts.report.rateOfSent', { rate: formatRate(deliveredRate) }) : t('broadcasts.report.noneSent')}
          loading={reportLoading && !report}
        />
        {whatsapp && (
          <MetricCard
            label={t('broadcasts.recipient.read')}
            value={formatCount(read)}
            hint={readRate != null ? t('broadcasts.report.rateOfSent', { rate: formatRate(readRate) }) : t('broadcasts.report.noneSent')}
            loading={reportLoading && !report}
          />
        )}
        <MetricCard
          label={t('status.failed')}
          value={formatCount(failed)}
          hint={failedRate != null && failed > 0 ? t('broadcasts.report.rateOfSent', { rate: formatRate(failedRate) }) : t('broadcasts.view.overview.failedHint')}
        />
        <MetricCard label={t('broadcasts.view.overview.filtered')} value={formatCount(filtered)} hint={t('broadcasts.view.overview.filteredHint')} />
        <div>
          <MetricCard
            label={t('broadcasts.view.overview.spend')}
            value={spendQ.isPending ? '…' : spend.net}
            hint={spend.detail
              ?? (spendRow && spendRow.entries > 0
                ? t('broadcasts.view.overview.spendHint', { each: credits(Math.round(spendRow.amount_minor / Math.max(1, spendRow.entries)), spendRow.currency) })
                : t('broadcasts.view.overview.spendNone'))}
          />
          <Link to="/billing/usage?period=90d" style={{ fontSize: 12.5, fontWeight: 600, textDecoration: 'none', color: 'var(--rr-color-primary, #0d9488)' }}>
            {t('broadcasts.view.overview.spendLink')}
          </Link>
        </div>
      </div>

      <ReportCard broadcast={broadcast} report={report} loading={reportLoading} onShowRecipients={onShowRecipients} />

      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('broadcasts.view.overview.progress')}</Title>
          <Text fz="xs" c={MUTED} data-testid="broadcast-progress">
            {progress.complete
              ? t('broadcasts.view.overview.progressComplete', { total: formatCount(progress.total) })
              : t('broadcasts.view.overview.progressDone', {
                done: formatCount(progress.done),
                total: formatCount(progress.total),
                pct: progress.pct,
                queued: formatCount(progress.queued)
              })}
          </Text>
          {throughput && (
            <Text fz="xs" c={MUTED}>
              {t('broadcasts.view.overview.throughput', {
                rate: throughput.perMinute,
                remaining: throughput.etaMinutes
              })}
            </Text>
          )}
          <div
            role="progressbar"
            aria-label={t('broadcasts.view.overview.progress')}
            aria-valuenow={progress.pct}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{
              height: 8,
              borderRadius: 999,
              background: 'var(--rr-color-surface-elevated, #1f2937)',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                width: `${progress.complete ? 100 : progress.pct}%`,
                height: '100%',
                background: progress.complete
                  ? 'var(--rr-color-success, #10b981)'
                  : 'var(--rr-color-primary, #0d9488)',
                transition: 'width 200ms ease' // i18n-ignore: CSS transition value, not copy
              }}
            />
          </div>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('broadcasts.view.overview.runDetails')}</Title>
          <Row
            label={t('common.status')}
            value={
              <Badge size="xs" variant="light" color={statusColor(broadcast.status_code)}>
                {broadcastStatusLabel(t, broadcast.status_code)}
              </Badge>
            }
          />
          {isEmailBroadcast(broadcast) ? (
            <>
              {/* An email broadcast has no template: its kind, its subject, its text and its button. */}
              <Row label={t('broadcasts.sms.channel.label')} value={t('broadcasts.email.channel')} />
              <Row label={t('broadcasts.email.kind.label')} value={emailKindName(t, broadcast)} />
              <Row
                label={t('broadcasts.email.subject.label')}
                value={<span style={{ overflowWrap: 'anywhere' }}>{broadcast.email_subject ?? ''}</span>}
              />
              <Row
                label={t('broadcasts.email.body.label')}
                value={<span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{broadcast.email_body ?? ''}</span>}
              />
              {broadcast.email_button_label && (
                <Row
                  label={t('broadcasts.email.button.label')}
                  value={<span style={{ overflowWrap: 'anywhere' }}>{`${broadcast.email_button_label} (${broadcast.email_button_url ?? ''})`}</span>}
                />
              )}
            </>
          ) : isSmsBroadcast(broadcast) ? (
            <>
              {/* An SMS broadcast has no template: the text it sent, its kind and its parts. */}
              <Row label={t('broadcasts.sms.channel.label')} value={t('broadcasts.sms.channel.sms')} />
              <Row label={t('broadcasts.sms.kind.label')} value={smsKindName(t, broadcast)} />
              <Row
                label={t('broadcasts.sms.body.label')}
                value={<span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{broadcast.sms_body ?? ''}</span>}
              />
              {broadcast.sms_segments != null && (
                <Row label={t('broadcasts.sms.review.parts')} value={String(broadcast.sms_segments)} />
              )}
            </>
          ) : (
            <>
              <Row label={t('broadcasts.columns.template')} value={<code>{broadcast.template_code}</code>} />
              <Row label={t('common.language')} value={<Badge size="xs" variant="light">{broadcast.template_language}</Badge>} />
            </>
          )}
          <Row label={t('broadcasts.view.overview.throttle')} value={`${broadcast.throttle_per_second}/s`} />
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('broadcasts.view.overview.timing')}</Title>
          <Row label={t('common.created')} value={when(broadcast.created_at)} />
          <Row
            label={t('status.scheduled')}
            value={broadcast.scheduled_at ? when(broadcast.scheduled_at) : <span style={{ color: MUTED }}>{t('broadcasts.view.overview.notScheduled')}</span>}
          />
          <Row label={t('broadcasts.view.lifecycle.started')} value={when(broadcast.started_at)} />
          <Row label={t('broadcasts.view.overview.finished')} value={when(broadcast.finished_at)} />
          {report?.last_delivered_at && (
            <Row label={t('broadcasts.view.overview.lastDelivered')} value={when(report.last_delivered_at)} />
          )}
          {whatsapp && report?.last_read_at && (
            <Row label={t('broadcasts.view.overview.lastRead')} value={when(report.last_read_at)} />
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text fz="xs" c={MUTED}>{label}</Text>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}
